import type { MembreEquipe } from '@/types/MembreEquipe'
import type { Contact, Dons, MotDuPasteur, SiteContent } from '@/types/Contenu'
import { requeteApi } from './apiClient'
import { versPredication, type PredicationApi } from './predicationsApi'

// Les réponses JSON-LD portent des clés techniques (@id, @type, @context) et des null pour
// les champs facultatifs vides : on les retire pour retrouver exactement les types du front.
type Ld<T> = T & { '@id'?: string; '@type'?: string; '@context'?: string }

const nettoyer = <T extends object>(objet: Ld<T>): T =>
  Object.fromEntries(Object.entries(objet).filter(([cle, valeur]) => !cle.startsWith('@') && valeur !== null)) as T

interface MembreApi {
  id: number
  nom: string
  fonction: string
  bio?: string | null
  photo: string
  position: number
}

const versMembre = (m: Ld<MembreApi>): MembreEquipe => {
  const { id, nom, fonction, bio, photo } = nettoyer(m)
  return { id: String(id), nom, fonction, bio: bio ?? undefined, photo }
}

interface ContenuApi {
  predications: Ld<PredicationApi>[]
  equipe: Ld<MembreApi>[]
  motDuPasteur: Ld<MotDuPasteur>
  dons: Ld<Dons>
  contact: Ld<Contact>
}

/** Tout le contenu du site en une requête (GET /api/contenu, revalidé par ETag). */
export async function lireContenu(): Promise<SiteContent> {
  const c = await requeteApi<ContenuApi>('/contenu')
  return {
    predications: c.predications.map((p) => versPredication(nettoyer(p))),
    equipe: c.equipe.map(versMembre),
    motDuPasteur: nettoyer(c.motDuPasteur),
    dons: nettoyer(c.dons),
    contact: nettoyer(c.contact),
  }
}

// ---------- Équipe ----------

const corpsMembre = (m: MembreEquipe) => ({
  nom: m.nom,
  fonction: m.fonction,
  // Champ vidé : null explicite, sinon merge-patch garderait l'ancienne valeur.
  bio: m.bio?.trim() || null,
  photo: m.photo,
})

export const creerMembre = async (m: MembreEquipe, position: number) =>
  versMembre(await requeteApi<MembreApi>('/equipe', { method: 'POST', corps: { ...corpsMembre(m), position } }))

export const modifierMembre = async (m: MembreEquipe) =>
  versMembre(await requeteApi<MembreApi>(`/equipe/${m.id}`, { method: 'PATCH', corps: corpsMembre(m) }))

export const supprimerMembre = async (id: string) => {
  await requeteApi(`/equipe/${id}`, { method: 'DELETE' })
}

export const changerPosition = async (id: string, position: number) => {
  await requeteApi(`/equipe/${id}`, { method: 'PATCH', corps: { position } })
}

// ---------- Ressources uniques ----------
// On envoie toujours l'objet complet : le formulaire de l'admin édite tous les champs à la fois.

export const enregistrerMotDuPasteur = async (mot: MotDuPasteur) =>
  nettoyer(
    await requeteApi<Ld<MotDuPasteur>>('/mot-du-pasteur', {
      method: 'PATCH',
      corps: { nom: mot.nom, fonction: mot.fonction, photo: mot.photo, videoId: mot.videoId || null, texte: mot.texte },
    }),
  )

export const enregistrerDons = async (dons: Dons) =>
  nettoyer(
    await requeteApi<Ld<Dons>>('/dons', {
      method: 'PATCH',
      corps: { mobileMoney: dons.mobileMoney, compteBancaire: dons.compteBancaire },
    }),
  )

export const enregistrerContact = async (contact: Contact) =>
  nettoyer(
    await requeteApi<Ld<Contact>>('/contact', {
      method: 'PATCH',
      corps: { ...contact, repere: contact.repere || null, email: contact.email || null },
    }),
  )
